import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { useLanguage } from '../contexts/LanguageContext'
import { useHotels, getLocalizedText } from '../hooks/useHotels'
import { ArrowLeft, MapPin, Star, Phone, Mail, Globe, ZoomIn } from 'lucide-react'
import { FullScreenImageModal } from '../components/TourDetails/FullScreenImageModal'

export function HotelDetail() {
  const { t } = useTranslation()
  const { language, isRTL } = useLanguage()
  const { id } = useParams()
  const { hotels, loading } = useHotels()
  const [selectedImage, setSelectedImage] = useState<string | null>(null)

  const hotel = hotels.find(h => h.id === id)

  useEffect(() => {
    if (hotel) {
      window.scrollTo(0, 0)
    }
  }, [id, hotel])

  // Show loading state while hotels are being fetched
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">{t('hotels.loading', 'Loading hotel...')}</p>
        </div>
      </div>
    )
  }

  // Only show not found after loading is complete
  if (!hotel) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-foreground mb-4">
            {t('hotels.notFound', 'Hotel not found')}
          </h2>
          <Link
            to="/hotels"
            className="text-primary hover:underline"
          >
            {t('hotels.backToHotels', 'Back to Hotels')}
          </Link>
        </div>
      </div>
    )
  }

  const mapUrl = hotel.latitude && hotel.longitude
    ? `https://www.google.com/maps?q=${hotel.latitude},${hotel.longitude}&output=embed`
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(getLocalizedText(hotel.address, language))}`

  return (
    <div className="min-h-screen bg-background">
      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-4 pt-24 pb-4">
        <Link
          to="/hotels"
          className={`inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
        >
          <ArrowLeft className="h-5 w-5" />
          <span>{t('hotels.backToHotels', 'Back to Hotels')}</span>
        </Link>
      </div>

      {/* Hotel Images Gallery */}
      {hotel.images && hotel.images.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {hotel.images.map((image, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
                className={`relative overflow-hidden rounded-xl cursor-pointer group ${
                  index === 0 ? 'md:col-span-2 md:row-span-2' : ''
                }`}
                onClick={() => setSelectedImage(image)}
              >
                <img
                  src={image}
                  alt={`${getLocalizedText(hotel.name, language)} - Image ${index + 1}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                  <ZoomIn className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Hotel Details */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Hotel Name and Rating */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="flex items-start justify-between mb-4">
                <h1 className="text-4xl font-bold text-foreground">
                  {getLocalizedText(hotel.name, language)}
                </h1>
                {hotel.rating && (
                  <div className="flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-lg">
                    <Star className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                    <span className="text-lg font-bold">{hotel.rating.toFixed(1)}</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 text-muted-foreground mb-6">
                <MapPin className="h-5 w-5" />
                <span>{getLocalizedText(hotel.city, language)}</span>
              </div>
            </motion.div>

            {/* Address */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-card border border-border rounded-xl p-6"
            >
              <h2 className="text-2xl font-bold text-foreground mb-4">
                {t('hotels.address', 'Address')}
              </h2>
              <p className="text-muted-foreground" dir={isRTL ? 'rtl' : 'ltr'}>
                {getLocalizedText(hotel.address, language)}
              </p>
            </motion.div>

            {/* Description */}
            {hotel.description && getLocalizedText(hotel.description, language) && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-card border border-border rounded-xl p-6"
              >
                <h2 className="text-2xl font-bold text-foreground mb-4">
                  {t('hotels.description', 'Description')}
                </h2>
                <p className="text-muted-foreground leading-relaxed" dir={isRTL ? 'rtl' : 'ltr'}>
                  {getLocalizedText(hotel.description, language)}
                </p>
              </motion.div>
            )}

            {/* Contact Information */}
            {(hotel.phone || hotel.email || hotel.website) && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-card border border-border rounded-xl p-6"
              >
                <h2 className="text-2xl font-bold text-foreground mb-4">
                  {t('hotels.contact', 'Contact Information')}
                </h2>
                <div className="space-y-3">
                  {hotel.phone && (
                    <div className="flex items-center gap-3">
                      <Phone className="h-5 w-5 text-primary" />
                      <a
                        href={`tel:${hotel.phone}`}
                        className="text-muted-foreground hover:text-primary transition-colors"
                      >
                        {hotel.phone}
                      </a>
                    </div>
                  )}
                  {hotel.email && (
                    <div className="flex items-center gap-3">
                      <Mail className="h-5 w-5 text-primary" />
                      <a
                        href={`mailto:${hotel.email}`}
                        className="text-muted-foreground hover:text-primary transition-colors"
                      >
                        {hotel.email}
                      </a>
                    </div>
                  )}
                  {hotel.website && (
                    <div className="flex items-center gap-3">
                      <Globe className="h-5 w-5 text-primary" />
                      <a
                        href={hotel.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted-foreground hover:text-primary transition-colors"
                      >
                        {hotel.website}
                      </a>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </div>

          {/* Map Sidebar */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-card border border-border rounded-xl overflow-hidden sticky top-8"
            >
              <h2 className="text-2xl font-bold text-foreground p-6 pb-0">
                {t('hotels.location', 'Location')}
              </h2>
              <div className="p-6">
                <div className="relative w-full h-64 rounded-lg overflow-hidden">
                  <iframe
                    src={mapUrl}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title={getLocalizedText(hotel.name, language)}
                  />
                </div>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(getLocalizedText(hotel.address, language))}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 block w-full text-center px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                >
                  {t('hotels.openInMaps', 'Open in Google Maps')}
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Full Screen Image Modal */}
      {selectedImage && (
        <FullScreenImageModal
          selectedImage={selectedImage}
          tourTitle={getLocalizedText(hotel.name, language)}
          onClose={() => setSelectedImage(null)}
        />
      )}
    </div>
  )
}

